import { Box, IconButton, Tooltip } from "@mui/material";
import TrashIcon from "../../../icons/TrashIcon";
import { semanticColors } from "../../../utils/colors";

// One entry of a list the professional can shorten: an email, a personal phone,
// an address. The entry's own content is whatever the caller renders; this only
// adds the remove action beside it.
//
// An entry the server would refuse to delete — the primary email, the last
// phone, the last practice address — is shown with the action disabled and the
// reason on hover, rather than letting the click travel just to come back as a
// 400.

interface Props {
  children: React.ReactNode;
  /** Why it cannot be removed. Absent means it can. */
  blockedReason?: string;
  removeLabel: string;
  onRemove: () => void;
}

export default function RemovableEntry({
  children,
  blockedReason,
  removeLabel,
  onRemove
}: Props) {
  const blocked = Boolean(blockedReason);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
      <Tooltip title={blockedReason ?? removeLabel}>
        {/* A disabled button fires no events, so the tooltip needs a wrapper
           that can still receive them. */}
        <Box component="span" sx={{ flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={onRemove}
            disabled={blocked}
            aria-label={removeLabel}
            sx={{
              color: blocked
                ? semanticColors.text.disabled
                : semanticColors.status.error
            }}
          >
            {/* The app's delete icon, as used to cancel a video consultation
               and to delete a clinical record — it inherits the colour set
               above so the disabled state reads as disabled. */}
            <TrashIcon size={16} />
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  );
}
